/**
 * Document Editor Component
 * A comprehensive document enhancement and OCR tool
 */

import React, { useState, useRef, useCallback, useEffect } from 'react'
import {
  Box,
  IconButton,
  Button,
  Typography,
  Slider,
  Tooltip,
  CircularProgress,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Tabs,
  Tab,
  Snackbar,
  Alert
} from '@mui/material'
import { styled } from '@mui/material/styles'
import {
  RotateLeft,
  RotateRight,
  Refresh,
  AutoFixHigh,
  Contrast,
  BrightnessHigh,
  FilterBAndW,
  InvertColors,
  Crop,
  ZoomIn,
  ZoomOut,
  Undo,
  Redo,
  Save,
  ContentCopy,
  TextFields,
  BlurOff,
  Close
} from '@mui/icons-material'

import {
  adjustBrightnessContrast,
  toGrayscale,
  toBlackWhiteOtsu,
  sharpen,
  autoEnhance,
  invert,
  denoise,
  rotateImage,
  loadImageToCanvas,
  canvasToBlob,
  canvasToDataURL
} from './documentEnhancer'
import { performOCR, OCR_LANGUAGES, copyToClipboard, terminateOCR } from './ocrService'

// Styled Components
const EditorContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  backgroundColor: '#1a1a2e',
  color: '#eee',
  overflow: 'hidden'
}))

const Header = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '12px 20px',
  backgroundColor: '#16213e',
  borderBottom: '1px solid #0f3460'
}))

const MainContent = styled(Box)({
  display: 'flex',
  flex: 1,
  overflow: 'hidden'
})

const CanvasArea = styled(Box)({
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'auto',
  backgroundColor: '#0f0f1a',
  position: 'relative'
})

const CanvasWrapper = styled(Box)(({ zoom }) => ({
  transform: `scale(${zoom})`,
  transformOrigin: 'center center',
  transition: 'transform 0.2s ease',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
  '& canvas': {
    display: 'block',
    maxWidth: 'none'
  }
}))

const Sidebar = styled(Box)({
  width: '280px',
  backgroundColor: '#16213e',
  borderLeft: '1px solid #0f3460',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden'
})

const ToolSection = styled(Box)({
  padding: '16px',
  '&:not(:last-child)': {
    borderBottom: '1px solid #0f3460'
  }
})

const ToolGrid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(4, 1fr)',
  gap: '8px',
  marginTop: '12px'
})

const ToolButton = styled(IconButton)(({ active }) => ({
  backgroundColor: active ? '#e94560' : 'rgba(255, 255, 255, 0.05)',
  color: active ? '#fff' : '#aaa',
  borderRadius: '8px',
  padding: '10px',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: active ? '#e94560' : 'rgba(255, 255, 255, 0.1)',
    color: '#fff',
    transform: 'translateY(-2px)'
  }
}))

const SliderContainer = styled(Box)({
  marginTop: '16px',
  '& .MuiSlider-root': {
    color: '#e94560'
  }
})

const OCRResultBox = styled(Paper)({
  flex: 1,
  margin: '16px',
  padding: '16px',
  backgroundColor: '#0f0f1a',
  color: '#eee',
  overflow: 'auto',
  fontFamily: 'monospace',
  fontSize: '14px',
  lineHeight: 1.6,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  border: '1px solid #0f3460',
  borderRadius: '8px'
})

const ActionButton = styled(Button)(({ variant: btnVariant }) => ({
  backgroundColor: btnVariant === 'primary' ? '#e94560' : 'transparent',
  color: '#fff',
  border: btnVariant === 'primary' ? 'none' : '1px solid #e94560',
  padding: '10px 24px',
  borderRadius: '8px',
  fontWeight: 600,
  textTransform: 'none',
  '&:hover': {
    backgroundColor: btnVariant === 'primary' ? '#d63750' : 'rgba(233, 69, 96, 0.1)'
  },
  '&:disabled': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    color: 'rgba(255, 255, 255, 0.3)'
  }
}))

const StyledTabs = styled(Tabs)({
  minHeight: '48px',
  backgroundColor: '#0f3460',
  '& .MuiTabs-indicator': {
    backgroundColor: '#e94560'
  }
})

const StyledTab = styled(Tab)({
  color: '#aaa',
  minHeight: '48px',
  textTransform: 'none',
  fontWeight: 500,
  '&.Mui-selected': {
    color: '#fff'
  }
})

/**
 * Document Editor Component
 */
export default function DocumentEditor({
  imageUrl,
  imageSrc,
  onSave,
  onClose,
  initialTab = 0
}) {
  const canvasRef = useRef(null)
  const originalCanvasRef = useRef(null)
  const historyRef = useRef([])
  const historyIndexRef = useRef(-1)

  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [loadError, setLoadError] = useState(null)
  const [activeTab, setActiveTab] = useState(initialTab)
  const [zoom, setZoom] = useState(1)
  const fileInputRef = useRef(null)

  // Enhancement settings
  const [brightness, setBrightness] = useState(0)
  const [contrast, setContrast] = useState(0)
  const [sharpness, setSharpness] = useState(0)

  // OCR state - default to English + Hindi for Indian documents
  const [ocrLanguage, setOcrLanguage] = useState('eng+hin')
  const [isOcrRunning, setIsOcrRunning] = useState(false)
  const [ocrProgress, setOcrProgress] = useState(0)
  const [ocrResult, setOcrResult] = useState(null)
  const [ocrConfidence, setOcrConfidence] = useState(null)

  // Notifications
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' })

  const source = imageUrl || imageSrc

  // State to track loaded image for rendering
  const [loadedCanvas, setLoadedCanvas] = useState(null)

  /**
   * Load image into canvas from various sources
   */
  const loadImageIntoEditor = useCallback(async (imageSource) => {
    console.log('Loading image:', imageSource?.substring(0, 100))
    setIsLoading(true)
    setLoadError(null)
    setLoadedCanvas(null)
    
    try {
      const { canvas } = await loadImageToCanvas(imageSource)
      console.log('Image loaded, size:', canvas.width, 'x', canvas.height)
      
      // Store original
      originalCanvasRef.current = document.createElement('canvas')
      originalCanvasRef.current.width = canvas.width
      originalCanvasRef.current.height = canvas.height
      originalCanvasRef.current.getContext('2d', { willReadFrequently: true }).drawImage(canvas, 0, 0)

      // Store loaded canvas to trigger re-render
      setLoadedCanvas(canvas)
      
      // Initialize history
      historyRef.current = [{
        imageData: canvas.getContext('2d', { willReadFrequently: true }).getImageData(0, 0, canvas.width, canvas.height),
        width: canvas.width,
        height: canvas.height
      }]
      historyIndexRef.current = 0

      // Auto-fit zoom based on a reasonable default
      setZoom(0.8)
      
      setLoadError(null)
    } catch (error) {
      console.error('Failed to load image:', error)
      setLoadError(error.message || 'Failed to load image')
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Draw loaded canvas to visible canvas ref when ready
  useEffect(() => {
    if (loadedCanvas && canvasRef.current) {
      console.log('Drawing to visible canvas')
      canvasRef.current.width = loadedCanvas.width
      canvasRef.current.height = loadedCanvas.height
      canvasRef.current.getContext('2d', { willReadFrequently: true }).drawImage(loadedCanvas, 0, 0)
    }
  }, [loadedCanvas])

  /**
   * Load image into canvas on mount
   */
  useEffect(() => {
    if (!source) return
    // Small delay to ensure canvas ref is attached
    const timer = setTimeout(() => {
      loadImageIntoEditor(source)
    }, 100)

    return () => {
      clearTimeout(timer)
      terminateOCR()
    }
  }, [source, loadImageIntoEditor])

  /**
   * Handle file upload for CORS fallback
   */
  const handleFileUpload = (event) => {
    const file = event.target.files?.[0]
    if (!file) return
    
    console.log('File selected:', file.name, file.type, file.size)

    const reader = new FileReader()
    reader.onload = (e) => {
      const dataUrl = e.target.result
      console.log('File read as data URL, length:', dataUrl.length)
      loadImageIntoEditor(dataUrl)
    }
    reader.onerror = (err) => {
      console.error('FileReader error:', err)
      showSnackbar('Failed to read file', 'error')
    }
    reader.readAsDataURL(file)
  }

  const triggerFileUpload = () => {
    console.log('Triggering file upload')
    fileInputRef.current?.click()
  }

  /**
   * Save current state to history
   */
  const saveToHistory = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas || canvas.width === 0 || canvas.height === 0) return

    try {
      const imageData = canvas.getContext('2d', { willReadFrequently: true }).getImageData(0, 0, canvas.width, canvas.height)
      
      // Remove any redo states
      historyRef.current = historyRef.current.slice(0, historyIndexRef.current + 1)
      
      // Add new state
      historyRef.current.push({
        imageData,
        width: canvas.width,
        height: canvas.height
      })
      historyIndexRef.current = historyRef.current.length - 1

      // Limit history
      if (historyRef.current.length > 20) {
        historyRef.current.shift()
        historyIndexRef.current--
      }
    } catch (err) {
      console.warn('Failed to save to history:', err)
    }
  }, [])

  /**
   * Apply an enhancement and save to history
   */
  const applyEnhancement = useCallback((enhanceFn) => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d', { willReadFrequently: true })
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
    const enhanced = enhanceFn(imageData)
    ctx.putImageData(enhanced, 0, 0)
    saveToHistory()
  }, [saveToHistory])

  /**
   * Quick enhancement tools
   */
  const handleAutoEnhance = () => applyEnhancement(autoEnhance)
  const handleGrayscale = () => applyEnhancement(toGrayscale)
  const handleBlackWhite = () => applyEnhancement(toBlackWhiteOtsu)
  const handleInvert = () => applyEnhancement(invert)
  const handleDenoise = () => applyEnhancement(denoise)
  const handleSharpen = () => applyEnhancement((data) => sharpen(data, 1))

  /**
   * Rotation
   */
  const handleRotateLeft = () => {
    const canvas = canvasRef.current
    if (canvas) {
      rotateImage(canvas, -90)
      saveToHistory()
    }
  }

  const handleRotateRight = () => {
    const canvas = canvasRef.current
    if (canvas) {
      rotateImage(canvas, 90)
      saveToHistory()
    }
  }

  /**
   * Apply brightness/contrast/sharpness sliders
   */
  const handleApplyAdjustments = () => {
    const canvas = canvasRef.current
    const original = originalCanvasRef.current
    if (!canvas || !original) return

    // Reset to original first
    canvas.width = original.width
    canvas.height = original.height
    canvas.getContext('2d', { willReadFrequently: true }).drawImage(original, 0, 0)

    const ctx = canvas.getContext('2d', { willReadFrequently: true })
    let imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)

    // Apply adjustments
    imageData = adjustBrightnessContrast(imageData, brightness, contrast)
    if (sharpness > 0) {
      imageData = sharpen(imageData, sharpness / 50)
    }

    ctx.putImageData(imageData, 0, 0)
    saveToHistory()
  }

  /**
   * Undo/Redo
   */
  const handleUndo = () => {
    if (historyIndexRef.current <= 0) return

    historyIndexRef.current--
    const state = historyRef.current[historyIndexRef.current]
    const canvas = canvasRef.current
    
    canvas.width = state.width
    canvas.height = state.height
    canvas.getContext('2d', { willReadFrequently: true }).putImageData(state.imageData, 0, 0)
  }

  const handleRedo = () => {
    if (historyIndexRef.current >= historyRef.current.length - 1) return

    historyIndexRef.current++
    const state = historyRef.current[historyIndexRef.current]
    const canvas = canvasRef.current

    canvas.width = state.width
    canvas.height = state.height
    canvas.getContext('2d', { willReadFrequently: true }).putImageData(state.imageData, 0, 0)
  }

  /**
   * Reset to original
   */
  const handleReset = () => {
    const canvas = canvasRef.current
    const original = originalCanvasRef.current
    if (!canvas || !original) return

    canvas.width = original.width
    canvas.height = original.height
    canvas.getContext('2d', { willReadFrequently: true }).drawImage(original, 0, 0)

    setBrightness(0)
    setContrast(0)
    setSharpness(0)
    saveToHistory()
    showSnackbar('Reset to original', 'info')
  }

  /**
   * Zoom controls
   */
  const handleZoomIn = () => setZoom((z) => Math.min(z + 0.25, 3))
  const handleZoomOut = () => setZoom((z) => Math.max(z - 0.25, 0.25))

  /**
   * OCR
   */
  const handleOCR = async () => {
    const canvas = canvasRef.current
    if (!canvas || canvas.width === 0 || canvas.height === 0) {
      showSnackbar('No image loaded. Please upload an image first.', 'warning')
      return
    }

    console.log('[OCR] Starting OCR on canvas:', canvas.width, 'x', canvas.height)
    setIsOcrRunning(true)
    setOcrProgress(0)
    setOcrResult(null)
    setOcrConfidence(null)

    try {
      const result = await performOCR(canvas, {
        language: ocrLanguage,
        onProgress: (progress) => {
          console.log('[OCR] Progress:', progress)
          setOcrProgress(progress)
        }
      })

      console.log('[OCR] Result:', result.text?.substring(0, 100))
      setOcrResult(result.text)
      setOcrConfidence(result.confidence)
      showSnackbar(`OCR completed with ${Math.round(result.confidence)}% confidence`, 'success')
    } catch (error) {
      console.error('[OCR] Failed:', error)
      showSnackbar('OCR failed: ' + (error.message || 'Please try again.'), 'error')
    } finally {
      setIsOcrRunning(false)
    }
  }

  const handleCopyOCRResult = async () => {
    if (ocrResult) {
      const success = await copyToClipboard(ocrResult)
      showSnackbar(success ? 'Copied to clipboard!' : 'Failed to copy', success ? 'success' : 'error')
    }
  }

  /**
   * Save
   */
  const handleSave = async () => {
    const canvas = canvasRef.current
    if (!canvas || !onSave) return

    setIsSaving(true)
    try {
      const blob = await canvasToBlob(canvas)
      const dataUrl = canvasToDataURL(canvas)
      await onSave({ blob, dataUrl, canvas })
      showSnackbar('Document saved successfully!', 'success')
    } catch (error) {
      console.error('Save failed:', error)
      showSnackbar('Failed to save document', 'error')
    } finally {
      setIsSaving(false)
    }
  }

  /**
   * Snackbar helper
   */
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity })
  }

  const closeSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }))
  }

  return (
    <EditorContainer>
      {/* Header */}
      <Header>
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#fff' }}>
          Document Editor
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Undo">
              <span>
                <IconButton 
                  onClick={handleUndo} 
                  sx={{ color: '#aaa' }}
                  disabled={historyIndexRef.current <= 0}
                >
                  <Undo />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Redo">
              <span>
                <IconButton 
                  onClick={handleRedo} 
                  sx={{ color: '#aaa' }}
                  disabled={historyIndexRef.current >= historyRef.current.length - 1}
                >
                  <Redo />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          <Divider orientation="vertical" flexItem sx={{ borderColor: '#0f3460' }} />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Zoom Out">
              <IconButton onClick={handleZoomOut} sx={{ color: '#aaa' }}>
                <ZoomOut />
              </IconButton>
            </Tooltip>
            <Typography sx={{ color: '#aaa', minWidth: '50px', textAlign: 'center', alignSelf: 'center' }}>
              {Math.round(zoom * 100)}%
            </Typography>
            <Tooltip title="Zoom In">
              <IconButton onClick={handleZoomIn} sx={{ color: '#aaa' }}>
                <ZoomIn />
              </IconButton>
            </Tooltip>
          </Box>
          <Divider orientation="vertical" flexItem sx={{ borderColor: '#0f3460' }} />
          <ActionButton variant="outlined" onClick={handleReset}>
            Reset
          </ActionButton>
          <ActionButton
            variant="primary"
            onClick={handleSave}
            disabled={isSaving || isLoading}
            startIcon={isSaving ? <CircularProgress size={18} color="inherit" /> : <Save />}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </ActionButton>
          {onClose && (
            <IconButton onClick={onClose} sx={{ color: '#aaa', ml: 1 }}>
              <Close />
            </IconButton>
          )}
        </Box>
      </Header>

      <MainContent>
        {/* Canvas Area */}
        <CanvasArea>
          {/* Hidden file input for CORS fallback */}
          <input
            type="file"
            ref={fileInputRef}
            accept="image/*"
            style={{ position: 'absolute', top: -9999, left: -9999 }}
            onChange={handleFileUpload}
            onClick={(e) => { e.target.value = null }} // Reset to allow same file selection
          />

          {isLoading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <CircularProgress sx={{ color: '#e94560' }} />
              <Typography sx={{ color: '#aaa' }}>Loading document...</Typography>
            </Box>
          ) : loadError ? (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              gap: 3,
              maxWidth: 400,
              textAlign: 'center',
              p: 4
            }}>
              <Box sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                backgroundColor: 'rgba(233, 69, 96, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Typography sx={{ fontSize: 40 }}>⚠️</Typography>
              </Box>
              <Typography sx={{ color: '#fff', fontSize: 18, fontWeight: 600 }}>
                Unable to Load Image
              </Typography>
              <Typography sx={{ color: '#aaa', fontSize: 14, lineHeight: 1.6 }}>
                {loadError}
              </Typography>
              <Typography sx={{ color: '#888', fontSize: 13, mb: 1 }}>
                You can upload a local copy of the image to edit it:
              </Typography>
              <Button
                variant="contained"
                onClick={() => {
                  console.log('Upload button clicked, fileInputRef:', fileInputRef.current)
                  if (fileInputRef.current) {
                    fileInputRef.current.click()
                  } else {
                    console.error('File input ref not available')
                  }
                }}
                sx={{
                  backgroundColor: '#e94560',
                  '&:hover': { backgroundColor: '#d63750' },
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 4,
                  py: 1.5,
                  fontSize: 16
                }}
              >
                📁 Upload Image from Device
              </Button>
              <Typography sx={{ color: '#666', fontSize: 12, fontStyle: 'italic', mt: 1 }}>
                Tip: Right-click the image in the list and "Save Image As...", then upload here
              </Typography>
            </Box>
          ) : loadedCanvas ? (
            <CanvasWrapper zoom={zoom}>
              <canvas 
                ref={canvasRef} 
                style={{ 
                  backgroundColor: '#fff',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
                }}
              />
            </CanvasWrapper>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <Typography sx={{ color: '#aaa' }}>No image loaded</Typography>
              <Button
                variant="contained"
                onClick={triggerFileUpload}
                sx={{
                  backgroundColor: '#e94560',
                  '&:hover': { backgroundColor: '#d63750' },
                  textTransform: 'none'
                }}
              >
                Upload Image
              </Button>
            </Box>
          )}
        </CanvasArea>

        {/* Sidebar */}
        <Sidebar>
          <StyledTabs
            value={activeTab}
            onChange={(e, v) => setActiveTab(v)}
            variant="fullWidth"
          >
            <StyledTab label="Enhance" />
            <StyledTab label="OCR" />
          </StyledTabs>

          {activeTab === 0 && (
            <Box sx={{ overflow: 'auto', flex: 1 }}>
              {/* Quick Tools */}
              <ToolSection>
                <Typography variant="subtitle2" sx={{ color: '#aaa', fontWeight: 600 }}>
                  Quick Tools
                </Typography>
                <ToolGrid>
                  <Tooltip title="Auto Enhance">
                    <ToolButton onClick={handleAutoEnhance}>
                      <AutoFixHigh />
                    </ToolButton>
                  </Tooltip>
                  <Tooltip title="Grayscale">
                    <ToolButton onClick={handleGrayscale}>
                      <Contrast />
                    </ToolButton>
                  </Tooltip>
                  <Tooltip title="Black & White">
                    <ToolButton onClick={handleBlackWhite}>
                      <FilterBAndW />
                    </ToolButton>
                  </Tooltip>
                  <Tooltip title="Invert">
                    <ToolButton onClick={handleInvert}>
                      <InvertColors />
                    </ToolButton>
                  </Tooltip>
                  <Tooltip title="Denoise">
                    <ToolButton onClick={handleDenoise}>
                      <BlurOff />
                    </ToolButton>
                  </Tooltip>
                  <Tooltip title="Sharpen">
                    <ToolButton onClick={handleSharpen}>
                      <BrightnessHigh />
                    </ToolButton>
                  </Tooltip>
                  <Tooltip title="Rotate Left">
                    <ToolButton onClick={handleRotateLeft}>
                      <RotateLeft />
                    </ToolButton>
                  </Tooltip>
                  <Tooltip title="Rotate Right">
                    <ToolButton onClick={handleRotateRight}>
                      <RotateRight />
                    </ToolButton>
                  </Tooltip>
                </ToolGrid>
              </ToolSection>

              {/* Adjustments */}
              <ToolSection>
                <Typography variant="subtitle2" sx={{ color: '#aaa', fontWeight: 600 }}>
                  Adjustments
                </Typography>

                <SliderContainer>
                  <Typography variant="caption" sx={{ color: '#888' }}>
                    Brightness: {brightness}
                  </Typography>
                  <Slider
                    value={brightness}
                    onChange={(e, v) => setBrightness(v)}
                    min={-100}
                    max={100}
                    size="small"
                  />
                </SliderContainer>

                <SliderContainer>
                  <Typography variant="caption" sx={{ color: '#888' }}>
                    Contrast: {contrast}
                  </Typography>
                  <Slider
                    value={contrast}
                    onChange={(e, v) => setContrast(v)}
                    min={-100}
                    max={100}
                    size="small"
                  />
                </SliderContainer>

                <SliderContainer>
                  <Typography variant="caption" sx={{ color: '#888' }}>
                    Sharpness: {sharpness}
                  </Typography>
                  <Slider
                    value={sharpness}
                    onChange={(e, v) => setSharpness(v)}
                    min={0}
                    max={100}
                    size="small"
                  />
                </SliderContainer>

                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleApplyAdjustments}
                  sx={{
                    mt: 2,
                    backgroundColor: '#e94560',
                    '&:hover': { backgroundColor: '#d63750' }
                  }}
                >
                  Apply Adjustments
                </Button>
              </ToolSection>
            </Box>
          )}

          {activeTab === 1 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
              <ToolSection>
                <Typography variant="subtitle2" sx={{ color: '#aaa', fontWeight: 600, mb: 2 }}>
                  Extract Text (OCR)
                </Typography>

                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                  <InputLabel sx={{ color: '#888' }}>Language</InputLabel>
                  <Select
                    value={ocrLanguage}
                    onChange={(e) => setOcrLanguage(e.target.value)}
                    label="Language"
                    sx={{
                      color: '#fff',
                      '.MuiOutlinedInput-notchedOutline': { borderColor: '#0f3460' },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#e94560' },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#e94560' },
                      '.MuiSvgIcon-root': { color: '#aaa' }
                    }}
                  >
                    {OCR_LANGUAGES.map((lang, index) => (
                      lang.disabled ? (
                        <MenuItem 
                          key={`divider-${index}`} 
                          disabled 
                          sx={{ 
                            fontSize: 12, 
                            color: '#666', 
                            fontWeight: 600,
                            opacity: 0.7,
                            py: 0.5
                          }}
                        >
                          {lang.name}
                        </MenuItem>
                      ) : (
                        <MenuItem 
                          key={lang.code} 
                          value={lang.code}
                          sx={{
                            backgroundColor: lang.recommended ? 'rgba(233, 69, 96, 0.1)' : 'transparent',
                            '&:hover': {
                              backgroundColor: lang.recommended ? 'rgba(233, 69, 96, 0.2)' : undefined
                            }
                          }}
                        >
                          {lang.name}
                        </MenuItem>
                      )
                    ))}
                  </Select>
                </FormControl>

                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleOCR}
                  disabled={isOcrRunning || isLoading || !loadedCanvas}
                  startIcon={
                    isOcrRunning ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <TextFields />
                    )
                  }
                  sx={{
                    backgroundColor: '#e94560',
                    '&:hover': { backgroundColor: '#d63750' }
                  }}
                >
                  {isOcrRunning ? `Processing... ${ocrProgress}%` : !loadedCanvas ? 'Load an image first' : 'Extract Text'}
                </Button>

                {ocrConfidence !== null && (
                  <Typography variant="caption" sx={{ color: '#888', mt: 1, display: 'block' }}>
                    Confidence: {Math.round(ocrConfidence)}%
                  </Typography>
                )}
              </ToolSection>

              {ocrResult && (
                <>
                  <Box sx={{ px: 2, py: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="subtitle2" sx={{ color: '#aaa' }}>
                      Extracted Text
                    </Typography>
                    <Tooltip title="Copy to Clipboard">
                      <IconButton onClick={handleCopyOCRResult} size="small" sx={{ color: '#aaa' }}>
                        <ContentCopy fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <OCRResultBox elevation={0}>
                    {ocrResult || 'No text extracted'}
                  </OCRResultBox>
                </>
              )}
            </Box>
          )}
        </Sidebar>
      </MainContent>

      {/* Snackbar Notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={closeSnackbar} severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </EditorContainer>
  )
}

